/**
 * RequireAuth - Route guard for authenticated routes
 * 
 * Ensures user is authenticated and has valid profile before allowing access.
 * Handles enterprise users, subscription checks, and email verification.
 */
import React, { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/auth';
import { auth } from '../../../firebase/firebase';
import AuthLoadingScreen from '../../shared/AuthLoadingScreen';

function RequireAuth({ children }) {
  const { user, loading, signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [initialDataLoading, setInitialDataLoading] = useState(true);
  const [profileVerified, setProfileVerified] = useState(false);
  const evInProgress = sessionStorage.getItem('email_verification_in_progress') === 'true';
  
  // Check if user is coming from login or signup
  const isComingFromAuth = 
    location.state?.from?.pathname === '/login' || 
    location.state?.from?.pathname === '/signup' ||
    sessionStorage.getItem('justLoggedIn') === 'true';
  
  // Check if user has no subscription (not premium)
  const hasNoSubscription = user && 
    (user.planLevel !== 'premium' && !user.isEnterprise);
  
  // Check if user is enterprise - ONLY by database flag, NOT by email domain
  const isEnterpriseUser = user?.isEnterpriseUser === true || user?.isEnterprise === true;

  console.log('RequireAuth subscription check:', {
    userId: user?.uid,
    planLevel: user?.planLevel,
    hasNoSubscription,
    isEnterpriseUser,
    path: location.pathname
  });
  
  // Check if we're on special pages that should still work without subscription
  const isOnSurveyPage = location.pathname.includes('/survey/');
  const isOnStockVideosPage = location.pathname === '/app/stockVideos';
  
  // CRITICAL: /app/account/pricing should be accessible to:
  // 1. All non-enterprise users (both with and without subscriptions)
  // 2. NOT accessible to enterprise users (who have enterprise-managed plans)
  // Users with subscriptions need it to manage/upgrade/downgrade their plans
  // Users without subscriptions need it to upgrade to paid plans
  const isOnRestrictedPricingPage = location.pathname === '/app/account/pricing';
  
  // Survey pages and stock videos are allowed without subscription (pricing page has its own logic)
  const isAllowedWithoutSubscription = isOnSurveyPage || isOnStockVideosPage;
  
  // When navigation completes, mark session as logged in
  useEffect(() => {
    if (user && isComingFromAuth) {
      sessionStorage.setItem('justLoggedIn', 'true');
      
      // Set a small timeout to allow data fetching to begin
      const timer = setTimeout(() => {
        setInitialDataLoading(false);
        // Clear the flag after transition is complete
        sessionStorage.removeItem('justLoggedIn');
      }, 1500); // Slightly longer delay for a smoother experience
      
      return () => clearTimeout(timer);
    } else if (user) {
      // If not coming from login, don't show extended loading
      setInitialDataLoading(false);
    }
  }, [user, isComingFromAuth]);

  // Verify user has a valid profile from our server
  useEffect(() => {
    const verifyProfile = async () => {
      if (user && !loading && !profileVerified) {
        // Check if the user has a proper profile
        const hasValidProfile = user.firstName || user.lastName || user.planLevel || user.subscription;
        
        if (!hasValidProfile) {
          console.warn('User account has no valid profile data. Redirecting to login...');
          // Sign out user and redirect to login
          try {
            await signOut(); // Use signOut from context instead of directly calling auth
          } catch (error) {
            console.error('Error signing out user with invalid profile:', error);
          }
          navigate('/login', { state: { 
            error: 'Your account is missing profile data. Please sign in again.'
          }});
          return;
        }
        
        setProfileVerified(true);
      }
    };
    
    verifyProfile();
  }, [user, loading, navigate, profileVerified, signOut]);

  // Improved redirect logic that's more consistent with NavigationHandler
  useEffect(() => {
    // Only run this effect if we have a verified profile and we're not already handling a redirect
    if (!user || loading || !profileVerified || initialDataLoading) {
      return;
    }
    
    // Check if account deletion is in progress - if so, don't redirect
    if (sessionStorage.getItem('accountDeletionInProgress') === 'true') {
      console.log('Account deletion in progress, skipping RequireAuth redirect');
      return;
    }
    
    // IMPORTANT: Don't redirect if user is on success page (they might be completing checkout)
    if (location.pathname.startsWith('/success')) {
      console.log('User is on success page, skipping RequireAuth redirect');
      return;
    }
    
    // Check if there's a specified redirect destination from login
    const redirectDestination = localStorage.getItem('auth_redirect_destination');
    
    if (redirectDestination) {
      // Clear the redirect destination to prevent future redirects
      localStorage.removeItem('auth_redirect_destination');
      console.log('Found redirect destination, navigating to:', redirectDestination);
      navigate(redirectDestination, { replace: true });
      return;
    }
    
    // Skip redirection if NavigationHandler is already handling it
    if (location.pathname.startsWith('/app') && location.pathname !== '/app/account/pricing') {
      console.log('User on app route, checking navigation handling');
    }
    
    // CRITICAL: Basic users should NOT access ANY /app routes EXCEPT allowed ones
    // Redirect them to public pricing page immediately
    if (hasNoSubscription && !isEnterpriseUser && location.pathname.startsWith('/app') && !isAllowedWithoutSubscription) {
    // Skip while email verification just finished to avoid bounce
    const evCompletedRecently = sessionStorage.getItem('email_verification_completed_recently') === 'true';
    if (evCompletedRecently) {
      console.log('RequireAuth: Skipping pricing redirect due to recent email verification completion');
      return;
    }
      console.log('Basic user trying to access restricted app routes - redirecting to public pricing page...', {
        planLevel: user.planLevel,
        isEnterpriseUser: user.isEnterpriseUser,
        hasNoSubscription,
        path: location.pathname,
        isAllowedWithoutSubscription
      });
      navigate('/pricing', { replace: true });
      return;
    }
  }, [user, loading, profileVerified, hasNoSubscription, isAllowedWithoutSubscription, navigate, initialDataLoading, location.pathname, isEnterpriseUser]);

  // Show AuthLoadingScreen when authenticating or loading initial data
  // IMPORTANT: Do NOT show the global AuthLoadingScreen on the public pricing page once
  // the user object exists – this caused the infinite spinner after Google sign-in.
  const isPublicPricing = location.pathname === '/pricing';
  if ((loading || evInProgress || (initialDataLoading && isComingFromAuth && user)) && !isPublicPricing) {
    // Using our custom AuthLoadingScreen instead of the default spinner
    return <AuthLoadingScreen />;
  }

  // If user is not authenticated, redirect to login
  if (!user) {
    // If account deletion is in progress, allow access to landing page
    if (sessionStorage.getItem('accountDeletionInProgress') === 'true' && location.pathname === '/') {
      console.log('Account deletion in progress, allowing access to landing page');
      return children;
    }
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // If profile verification is still pending, show AuthLoadingScreen
  if (!profileVerified) {
    // Using our custom AuthLoadingScreen instead of the default spinner
    return <AuthLoadingScreen />;
  }

  // CRITICAL: Check if enterprise user needs email verification before allowing app access
  if (isEnterpriseUser && auth.currentUser && !auth.currentUser.emailVerified) {
    console.log('RequireAuth: Enterprise user with unverified email, redirecting to verification page');
    return <Navigate to="/verify-email" replace />;
  }

  // CRITICAL: Handle access to /app/account/pricing page
  if (isOnRestrictedPricingPage) {
    console.log('RequireAuth: User trying to access restricted pricing page', {
      isEnterpriseUser,
      hasNoSubscription,
      planLevel: user?.planLevel
    });
    
    // Enterprise users should NEVER access /app/account/pricing
    if (isEnterpriseUser) {
      console.log('RequireAuth: Enterprise user trying to access /app/account/pricing, redirecting to app');
      return <Navigate to="/app/" replace />;
    }
    
    // All non-enterprise users (both with and without subscriptions) can access this page
    // Users with subscriptions need it to manage/upgrade/downgrade
    // Users without subscriptions need it to upgrade
    console.log('RequireAuth: Allowing non-enterprise user to access pricing page');
  }

  // CRITICAL: Basic users should already be redirected by the useEffect above
  // This loading screen is no longer needed since basic users won't reach this point
  // Only keep loading for legitimate subscription checks

  return children;
}

export default RequireAuth;

